/**
 * Display preference types, defaults, allowed-value constants, resolve helper,
 * and the no-flash inline-script source string.
 *
 * This module is pure data — no React, no DOM, no side-effects at import time.
 * Safe for server rendering and the sessionless register bundle.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ThemeMode = 'light' | 'dark' | 'system'
export type Accent = 'authority' | 'fjord' | 'sand' | 'slate' | 'aurora' | 'gold' | 'terminal' | 'civic' | 'friday'
export type Font = 'source' | 'plex' | 'atkinson' | 'system'
export type TextSize = 'sm' | 'md' | 'lg'
export type Density = 'compact' | 'normal' | 'comfy'
export type Contrast = 'normal' | 'high'
export type Crisp = 'off' | 'on'
export type Motion = 'full' | 'reduced'
export type SidebarState = 'expanded' | 'collapsed'
export type CaseWorkMode = 'v2'

export interface DisplayPreferences {
  theme: ThemeMode
  accent: Accent
  /** Saturation of the admin interface, from greyscale (0) to full colour (100). */
  colorIntensity: number
  font: Font
  textSize: TextSize
  density: Density
  contrast: Contrast
  crisp: Crisp
  motion: Motion
  /** Admin sidebar collapsed to the icon-only rail, or at full width. */
  sidebar: SidebarState
  /** The sole case-detail workspace. Retained to normalize stored preferences. */
  caseWorkMode: CaseWorkMode
  /** Admin sidebar width in pixels when expanded, clamped to the bounds below. */
  sidebarWidth: number
  /** Admin case-detail LEFT (metadata) column width in pixels. */
  caseLeftWidth: number
  /** Admin case-detail RIGHT (actions) column width in pixels. */
  caseRightWidth: number
}

// ---------------------------------------------------------------------------
// Allowed-values constants
// ---------------------------------------------------------------------------

export const ALLOWED_THEMES: readonly ThemeMode[] = ['light', 'dark', 'system'] as const
export const ALLOWED_ACCENTS: readonly Accent[] = ['authority', 'fjord', 'sand', 'slate', 'aurora', 'gold', 'terminal', 'civic', 'friday'] as const
export const ALLOWED_FONTS: readonly Font[] = ['source', 'plex', 'atkinson', 'system'] as const
export const ALLOWED_TEXT_SIZES: readonly TextSize[] = ['sm', 'md', 'lg'] as const
export const ALLOWED_DENSITIES: readonly Density[] = ['compact', 'normal', 'comfy'] as const
export const ALLOWED_CONTRASTS: readonly Contrast[] = ['normal', 'high'] as const
export const ALLOWED_CRISPS: readonly Crisp[] = ['off', 'on'] as const
export const ALLOWED_MOTIONS: readonly Motion[] = ['full', 'reduced'] as const
export const ALLOWED_SIDEBAR_STATES: readonly SidebarState[] = ['expanded', 'collapsed'] as const
export const ALLOWED_CASE_WORK_MODES: readonly CaseWorkMode[] = ['v2'] as const

/** Sidebar width bounds. Mirrors HasDisplayPreferences::NUMERIC_RANGES server-side. */
export const SIDEBAR_WIDTH_MIN = 180
export const SIDEBAR_WIDTH_MAX = 420

/**
 * Case-detail side-column bounds. Same range for both columns: the low end
 * keeps a column readable rather than a sliver, the high end leaves the centre
 * document pane its own `minmax(360px, …)` floor on a normal workstation.
 */
export const CASE_COLUMN_WIDTH_MIN = 220
export const CASE_COLUMN_WIDTH_MAX = 640

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

export const DEFAULTS: DisplayPreferences = {
  theme: 'light',
  accent: 'aurora',
  colorIntensity: 100,
  font: 'source',
  textSize: 'sm',
  density: 'compact',
  contrast: 'normal',
  crisp: 'off',
  motion: 'full',
  sidebar: 'expanded',
  caseWorkMode: 'v2',
  sidebarWidth: 232,
  caseLeftWidth: 280,
  caseRightWidth: 320,
}

/**
 * Coerces any input to a finite integer: rounds, clamps to the bounds, and
 * falls back to `fallback` for anything non-numeric. The server rejects
 * out-of-range values, so clamping here keeps a stale or hand-edited
 * localStorage value from producing a request that 422s on every change.
 */
function clampNumber(value: unknown, min: number, max: number, fallback: number): number {
  const n = typeof value === 'string' ? Number(value) : value
  if (typeof n !== 'number' || !Number.isFinite(n)) return fallback
  return Math.min(max, Math.max(min, Math.round(n)))
}

export function clampSidebarWidth(value: unknown): number {
  return clampNumber(value, SIDEBAR_WIDTH_MIN, SIDEBAR_WIDTH_MAX, DEFAULTS.sidebarWidth)
}

/** Clamps a case-detail side-column width; `fallback` is that column's default. */
export function clampCaseColumnWidth(value: unknown, fallback: number): number {
  return clampNumber(value, CASE_COLUMN_WIDTH_MIN, CASE_COLUMN_WIDTH_MAX, fallback)
}

export function clampColorIntensity(value: unknown): number {
  if (typeof value === 'string' && value.trim() === '') return DEFAULTS.colorIntensity
  return clampNumber(value, 0, 100, DEFAULTS.colorIntensity)
}

/** No filter at the default, so the existing rendering stays untouched. */
export function colorIntensityFilter(value: number): string {
  const intensity = clampColorIntensity(value)
  return intensity === 100 ? 'none' : `saturate(${intensity}%)`
}

// ---------------------------------------------------------------------------
// localStorage key prefix
// ---------------------------------------------------------------------------

/** All display-preference keys in localStorage are prefixed with this string. */
export const LS_PREFIX = 'mbn:display:' as const

// ---------------------------------------------------------------------------
// resolve() — fill defaults for a partial prefs object
// ---------------------------------------------------------------------------

/**
 * Returns a fully-defaulted DisplayPreferences from a partial/untrusted input.
 * Unknown or invalid values are replaced by the corresponding default.
 */
export function resolve(partial: Partial<DisplayPreferences>): DisplayPreferences {
  return {
    theme: ALLOWED_THEMES.includes(partial.theme as ThemeMode)
      ? (partial.theme as ThemeMode)
      : DEFAULTS.theme,
    accent: ALLOWED_ACCENTS.includes(partial.accent as Accent)
      ? (partial.accent as Accent)
      : DEFAULTS.accent,
    colorIntensity: clampColorIntensity(partial.colorIntensity),
    font: ALLOWED_FONTS.includes(partial.font as Font)
      ? (partial.font as Font)
      : DEFAULTS.font,
    textSize: ALLOWED_TEXT_SIZES.includes(partial.textSize as TextSize)
      ? (partial.textSize as TextSize)
      : DEFAULTS.textSize,
    density: ALLOWED_DENSITIES.includes(partial.density as Density)
      ? (partial.density as Density)
      : DEFAULTS.density,
    contrast: ALLOWED_CONTRASTS.includes(partial.contrast as Contrast)
      ? (partial.contrast as Contrast)
      : DEFAULTS.contrast,
    crisp: ALLOWED_CRISPS.includes(partial.crisp as Crisp)
      ? (partial.crisp as Crisp)
      : DEFAULTS.crisp,
    motion: ALLOWED_MOTIONS.includes(partial.motion as Motion)
      ? (partial.motion as Motion)
      : DEFAULTS.motion,
    sidebar: ALLOWED_SIDEBAR_STATES.includes(partial.sidebar as SidebarState)
      ? (partial.sidebar as SidebarState)
      : DEFAULTS.sidebar,
    caseWorkMode: ALLOWED_CASE_WORK_MODES.includes(partial.caseWorkMode as CaseWorkMode)
      ? (partial.caseWorkMode as CaseWorkMode)
      : DEFAULTS.caseWorkMode,
    sidebarWidth: clampSidebarWidth(partial.sidebarWidth),
    caseLeftWidth: clampCaseColumnWidth(partial.caseLeftWidth, DEFAULTS.caseLeftWidth),
    caseRightWidth: clampCaseColumnWidth(partial.caseRightWidth, DEFAULTS.caseRightWidth),
  }
}

// ---------------------------------------------------------------------------
// No-flash inline-script source strings
// ---------------------------------------------------------------------------

/**
 * Renders an allowed-value list as a JS array literal. Single-quoted so the
 * emitted source stays embeddable verbatim inside a double-quoted host string —
 * the layouts inline it that way.
 */
const jsList = (values: readonly string[]): string => `[${values.map((v) => `'${v}'`).join(',')}]`

/**
 * Opening of every no-flash variant: reads a preference (server-supplied JSON
 * attribute first, then localStorage) and writes a validated value — or the
 * default — onto <html>.
 */
const NO_FLASH_PRELUDE: string = /* js */ `
  var p='${LS_PREFIX}';
  var h=document.documentElement;
  var S={};try{S=JSON.parse(h.getAttribute('data-display-prefs')||'{}')||{};}catch(e){}
  function g(k){return S[k]!=null?S[k]:localStorage.getItem(p+k);}
  function set(attr,k,allowed,def){
    var v=g(k);
    h.setAttribute(attr,allowed.indexOf(v)!==-1?v:def);
  }`

/**
 * Reading comfort — type size, density and the readability switches. Every
 * surface resolves these before paint: they reflow or repaint the whole page,
 * so applying them once React mounts is a visible jump.
 */
const NO_FLASH_READING_ATTRS: string = /* js */ `
  set('data-font-scale','textSize',${jsList(ALLOWED_TEXT_SIZES)},'${DEFAULTS.textSize}');
  set('data-density','density',${jsList(ALLOWED_DENSITIES)},'${DEFAULTS.density}');
  set('data-contrast','contrast',${jsList(ALLOWED_CONTRASTS)},'${DEFAULTS.contrast}');
  set('data-crisp','crisp',${jsList(ALLOWED_CRISPS)},'${DEFAULTS.crisp}');
  set('data-motion','motion',${jsList(ALLOWED_MOTIONS)},'${DEFAULTS.motion}');`

/**
 * Minified inline script source that reads localStorage display preferences
 * and sets data-* attributes on <html> BEFORE first paint, preventing the
 * flash of wrong theme/density/etc.
 *
 * Inject this as `<script dangerouslySetInnerHTML={{ __html: NO_FLASH_SOURCE }}`
 * in the <head> of the Inertia root layout (app/views/layouts/inertia.html.erb).
 *
 * The script runs synchronously (no defer/async) and relies only on
 * localStorage and document.documentElement — no React, no module system.
 *
 * When the server provides saved preferences (signed-in users) via the
 * `data-display-prefs` JSON attribute on <html>, the script prefers those over
 * localStorage — the same priority ThemeProvider applies once React mounts, so
 * the first painted frame already matches the mounted app.
 *
 * Keys read: `mbn:display:theme`, `mbn:display:accent`, `mbn:display:font`,
 * `mbn:display:textSize`, `mbn:display:density`, `mbn:display:contrast`,
 * `mbn:display:crisp`, `mbn:display:motion`, `mbn:display:sidebarWidth`,
 * `mbn:display:caseLeftWidth`, `mbn:display:caseRightWidth`, `mbn:display:colorIntensity`.
 *
 * The sidebar and case-column widths are written as the `--sidebar-w`,
 * `--case-left-w` and `--case-right-w` custom properties on <html> rather than
 * data-* attributes, so the shell grid and the case layout pick up the stored
 * widths in the first painted frame instead of snapping from the token values.
 * The collapsed state needs no pre-paint work: it is a class on the shell root,
 * which does not exist until React mounts.
 *
 * For theme=system the script resolves via prefers-color-scheme immediately
 * (no listener — listeners are set up by ThemeProvider at runtime).
 */
export const NO_FLASH_SOURCE: string = /* js */ `(function(){${NO_FLASH_PRELUDE}
  var theme=g('theme');
  var resolvedTheme;
  if(${jsList(ALLOWED_THEMES)}.indexOf(theme)!==-1){
    if(theme==='system'){
      resolvedTheme=window.matchMedia&&window.matchMedia('(prefers-color-scheme: dark)').matches?'dark':'light';
    }else{
      resolvedTheme=theme;
    }
  }else{
    resolvedTheme='${DEFAULTS.theme}';
  }
  h.setAttribute('data-theme',resolvedTheme);
  set('data-accent','accent',${jsList(ALLOWED_ACCENTS)},'${DEFAULTS.accent}');
  var intensity=g('colorIntensity');
  intensity=typeof intensity==='number'||typeof intensity==='string'&&intensity.trim()!==''?Number(intensity):NaN;
  intensity=isFinite(intensity)?Math.min(100,Math.max(0,Math.round(intensity))):100;
  h.style.setProperty('--display-color-filter',intensity===100?'none':'saturate('+intensity+'%)');
  set('data-font','font',${jsList(ALLOWED_FONTS)},'${DEFAULTS.font}');${NO_FLASH_READING_ATTRS}
  function setw(prop,k,min,max,def){
    var w=Number(g(k));
    h.style.setProperty(prop,(isFinite(w)&&w?Math.min(max,Math.max(min,Math.round(w))):def)+'px');
  }
  setw('--sidebar-w','sidebarWidth',${SIDEBAR_WIDTH_MIN},${SIDEBAR_WIDTH_MAX},${DEFAULTS.sidebarWidth});
  setw('--case-left-w','caseLeftWidth',${CASE_COLUMN_WIDTH_MIN},${CASE_COLUMN_WIDTH_MAX},${DEFAULTS.caseLeftWidth});
  setw('--case-right-w','caseRightWidth',${CASE_COLUMN_WIDTH_MIN},${CASE_COLUMN_WIDTH_MAX},${DEFAULTS.caseRightWidth});
})()`

/**
 * Portal variant of the no-flash script.
 *
 * The public surface wears one fixed look — a light ground and one type family —
 * so theme, accent and font are never resolved here: the script paints
 * `data-theme="light"` unconditionally. A visitor who once chose dark or an
 * accent on the internal workstation keeps that stored preference; it simply has
 * no effect on this surface, and no dark frame is painted on the way in.
 *
 * Reading comfort is still resolved before paint (see NO_FLASH_READING_ATTRS).
 * The sidebar width is not: the portal has no sidebar.
 */
export const PORTAL_NO_FLASH_SOURCE: string = /* js */ `(function(){${NO_FLASH_PRELUDE}
  h.setAttribute('data-theme','light');${NO_FLASH_READING_ATTRS}
})()`
